import type { AppCampaignConfig, AppConfiguration } from '@/types/api';
import type { MetaAd, MetaAdSet } from '@/types/metaCampaign';

// Pure helpers for splicing Meta entities into the React Query
// `['app-config', memberUuid]` cache after create/update mutations.
//
// Scope: append + apply helpers only. The entity **shape** is now
// server-authoritative — the backend passes `response.applied_entity`
// through as a raw dict on create responses; earlier iterations of this
// module carried `buildOptimisticMeta*Entry` builders that reconstructed
// the shape from the request payload, but those were deleted once the
// backend enrichment shipped (see `docs/FRONTEND_MUTATION_PATTERNS.md`).
// Downstream reader is `buildMetaCampaignFromConfig` in
// `useCampaignFromCache.ts`, which consumes what these helpers write.
//
// Rules every helper follows:
//   - Never mutate the input; return a new AppConfiguration (React Query
//     compares by reference).
//   - No-op (return `prev`) when `prev` is missing, the target parent isn't
//     in the cache, or the entity id is already present. Idempotent so a
//     later refetch that includes the entity doesn't duplicate it.
//   - Only ever *add* / *patch* entries — never remove. Rollback on failure
//     isn't needed because callers only invoke these after the API returns
//     200 (creates) or after `successful_updates` is walked (batch).

const sameId = (a: string | null | undefined, b: string | null | undefined): boolean =>
  String(a ?? '') === String(b ?? '');

export function appendOptimisticMetaCampaign(
  prev: AppConfiguration | undefined,
  entry: AppCampaignConfig,
): AppConfiguration | undefined {
  if (!prev?.configuration?.campaign_list) return prev;
  const list = prev.configuration.campaign_list;
  if (list.some((c) => sameId(c.campaign_id, entry.campaign_id))) return prev;
  return {
    ...prev,
    configuration: {
      ...prev.configuration,
      campaign_list: [...list, entry],
    },
  };
}

export function appendOptimisticMetaAdSet(
  prev: AppConfiguration | undefined,
  campaignId: string,
  entry: MetaAdSet,
): AppConfiguration | undefined {
  if (!prev?.configuration?.campaign_list) return prev;
  let mutated = false;
  const nextList = prev.configuration.campaign_list.map((c) => {
    if (!sameId(c.campaign_id, campaignId)) return c;
    const existing = c.ad_sets ?? [];
    if (existing.some((s) => sameId(s.ad_set_id, entry.ad_set_id))) return c;
    mutated = true;
    return { ...c, ad_sets: [...existing, entry] };
  });
  if (!mutated) return prev;
  return {
    ...prev,
    configuration: {
      ...prev.configuration,
      campaign_list: nextList,
    },
  };
}

export function appendOptimisticMetaAd(
  prev: AppConfiguration | undefined,
  adSetId: string,
  entry: MetaAd,
): AppConfiguration | undefined {
  if (!prev?.configuration?.campaign_list) return prev;
  let mutated = false;
  const nextList = prev.configuration.campaign_list.map((c) => {
    if (!c.ad_sets?.length) return c;
    let campaignMutated = false;
    const nextAdSets = c.ad_sets.map((s) => {
      if (!sameId(s.ad_set_id, adSetId)) return s;
      const existing = s.ads ?? [];
      if (existing.some((a) => sameId(a.ad_id, entry.ad_id))) return s;
      campaignMutated = true;
      mutated = true;
      return { ...s, ads: [...existing, entry] };
    });
    return campaignMutated ? { ...c, ad_sets: nextAdSets } : c;
  });
  if (!mutated) return prev;
  return {
    ...prev,
    configuration: {
      ...prev.configuration,
      campaign_list: nextList,
    },
  };
}

// Map the batch-update `action` verb to Meta's status vocabulary. Kept in one
// place so save-changes-bar reconciliation and any future ad-set/ad status UI
// share a single translation.
export function metaActionToStatus(
  action: 'pause' | 'enable' | 'archive',
): 'PAUSED' | 'ACTIVE' | 'ARCHIVED' {
  if (action === 'pause') return 'PAUSED';
  if (action === 'enable') return 'ACTIVE';
  return 'ARCHIVED';
}

/** Update campaign/ad_set/ad status in-place (immutably) in the app-config
 *  cache after a batchUpdate status change succeeds. No-ops when the target
 *  isn't found — the next natural refetch will reconcile. */
export function applyMetaStatusChange(
  prev: AppConfiguration | undefined,
  entityType: 'campaign' | 'ad_set' | 'ad',
  entityId: string,
  newStatus: string,
): AppConfiguration | undefined {
  if (!prev?.configuration?.campaign_list) return prev;
  let mutated = false;
  const nextList = prev.configuration.campaign_list.map((c) => {
    if (entityType === 'campaign' && sameId(c.campaign_id, entityId)) {
      mutated = true;
      return {
        ...c,
        status: newStatus as AppCampaignConfig['status'],
        campaign_status: newStatus,
      };
    }
    if (!c.ad_sets?.length) return c;
    let campaignMutated = false;
    const nextAdSets = c.ad_sets.map((s) => {
      if (entityType === 'ad_set' && sameId(s.ad_set_id, entityId)) {
        campaignMutated = true;
        mutated = true;
        return { ...s, status: newStatus };
      }
      if (!s.ads?.length) return s;
      let adSetMutated = false;
      const nextAds = s.ads.map((a) => {
        if (entityType === 'ad' && sameId(a.ad_id, entityId)) {
          adSetMutated = true;
          campaignMutated = true;
          mutated = true;
          return { ...a, status: newStatus, effective_status: newStatus };
        }
        return a;
      });
      return adSetMutated ? { ...s, ads: nextAds } : s;
    });
    return campaignMutated ? { ...c, ad_sets: nextAdSets } : c;
  });
  if (!mutated) return prev;
  return {
    ...prev,
    configuration: { ...prev.configuration, campaign_list: nextList },
  };
}

export function applyMetaCampaignRename(
  prev: AppConfiguration | undefined,
  campaignId: string,
  newName: string,
): AppConfiguration | undefined {
  if (!prev?.configuration?.campaign_list) return prev;
  let mutated = false;
  const nextList = prev.configuration.campaign_list.map((c) => {
    if (!sameId(c.campaign_id, campaignId)) return c;
    mutated = true;
    return { ...c, campaign_name: newName };
  });
  if (!mutated) return prev;
  return {
    ...prev,
    configuration: { ...prev.configuration, campaign_list: nextList },
  };
}

/** Rename an ad in-place (immutably) in the app-config cache. Mirrors
 *  `applyMetaCampaignRename` at the ad grain — used by the ad editor so
 *  a name change surfaces in `AdsTab` before the app-config refetch
 *  lands. No-ops when the ad isn't found. */
export function applyMetaAdRename(
  prev: AppConfiguration | undefined,
  adId: string,
  newName: string,
): AppConfiguration | undefined {
  if (!prev?.configuration?.campaign_list) return prev;
  let mutated = false;
  const nextList = prev.configuration.campaign_list.map((c) => {
    if (!c.ad_sets?.length) return c;
    let campaignMutated = false;
    const nextAdSets = c.ad_sets.map((s) => {
      if (!s.ads?.length) return s;
      let adSetMutated = false;
      const nextAds = s.ads.map((a) => {
        if (!sameId(a.ad_id, adId)) return a;
        adSetMutated = true;
        campaignMutated = true;
        mutated = true;
        return { ...a, ad_name: newName };
      });
      return adSetMutated ? { ...s, ads: nextAds } : s;
    });
    return campaignMutated ? { ...c, ad_sets: nextAdSets } : c;
  });
  if (!mutated) return prev;
  return {
    ...prev,
    configuration: { ...prev.configuration, campaign_list: nextList },
  };
}

export function applyMetaCampaignBudget(
  prev: AppConfiguration | undefined,
  campaignId: string,
  newDailyBudget: number,
): AppConfiguration | undefined {
  if (!prev?.configuration?.campaign_list) return prev;
  let mutated = false;
  const nextList = prev.configuration.campaign_list.map((c) => {
    if (!sameId(c.campaign_id, campaignId)) return c;
    mutated = true;
    // Patch both `daily_budget` (Meta-canonical) AND `budget` (the
    // cross-platform field the Campaign detail sidebar's "Daily Budget"
    // display and any allocatable-budget readers derive from). Keeping
    // them in sync mirrors what the create-campaign backend writes
    // (`'budget': budget_dollars` alongside `'daily_budget'` in
    // `create_meta_campaign_endpoint`) — without this, the sidebar
    // shows the pre-save value until the next natural refetch.
    return { ...c, daily_budget: newDailyBudget, budget: newDailyBudget };
  });
  if (!mutated) return prev;
  return {
    ...prev,
    configuration: { ...prev.configuration, campaign_list: nextList },
  };
}
